import { useEffect } from 'react'
import { useGetMyPermissionsQuery } from '@/api/permissions.api'
import { useGetCurrentTenantQuery } from '@/api/tenants.api'
import { useAuthStore } from '@/store/auth.store'

/**
 * Enriches the JWT-derived user with data the token doesn't carry: the tenant's
 * display name and the caller's effective permissions.
 *
 * Permissions come from `/permissions/me`, which any authenticated user may
 * read. The previous route — list roles, match the role name, then read that
 * role's permissions — went through two `role:read` endpoints, so it 403'd for
 * every non-admin and left `permissions` permanently empty. That empty list is
 * what forced the nav to fail open; see useManifest.
 */
export function useSessionEnrichment(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const { data: tenant } = useGetCurrentTenantQuery(undefined, { skip: !isAuthenticated })
  const { data: myPermissions } = useGetMyPermissionsQuery(undefined, { skip: !isAuthenticated })

  useEffect(() => {
    const state = useAuthStore.getState()
    const user = state.user
    if (!user) return

    const tenantName = tenant?.name ?? user.tenantName
    const permissions = myPermissions
      ? myPermissions.map((p) => `${p.resource}:${p.action}`)
      : user.permissions

    const permsChanged =
      permissions.length !== user.permissions.length ||
      permissions.some((p, i) => p !== user.permissions[i])

    if (tenantName !== user.tenantName || permsChanged) {
      state.setUser({ ...user, tenantName, permissions })
    }
  }, [tenant, myPermissions])
}
